<template>
  <div>
    <b-row align-h="center">
      <b-col>
        <b-button-close @click="closeDetails">
          <b-icon icon="file-x-fill"></b-icon>
        </b-button-close>
        <h3 class="mb-n1">
          {{ currentProject.course }}
        </h3>
        <h5>
          <span class="text-muted"
            >{{ currentProject.id }}:
            {{ currentProject.developer_lastname }}</span
          >
        </h5>
        <b-card class="mt-3" no-body>
          <b-tabs v-model="activeTab" card>
            <b-tab card="true" title="Project details">
              <div
                v-if="
                  isOFDITLead ||
                    (currentUser.is_grizzly_admin &&
                      currentUser.program === 'OFDIT')
                "
              >
                <b-button
                  @click="toggleCommentForm"
                  variant="outline-primary"
                  class="mx-3 my-3"
                >
                  <b-icon icon="book"></b-icon>
                  Toggle edit form
                </b-button>
                <b-dd variant="outline-primary" class="mx-3 my-3">
                  <template #button-content>
                    <b-icon icon="briefcase"> </b-icon>
                    Project actions
                  </template>
                  <b-dd-item
                    :to="{
                      name: 'setupReview',
                      params: { projectId: currentProject.id },
                    }"
                    >Setup review</b-dd-item
                  >
                  <b-dd-item disabled>Create support request</b-dd-item>
                  <b-dd-item disabled>Reassign OFDIT lead</b-dd-item>
                </b-dd>
                <GProjectDetailsForm
                  v-if="showCommentForm"
                  class="pb-3"
                  :projectStatus="currentProject.project_status"
                  :deliveryDate="currentProject.delivery_date.substring(0, 10)"
                  :targetLaunchSemester="currentProject.target_launch_semester"
                ></GProjectDetailsForm>
              </div>
              <div v-else class="mb-3" />
              <b-row class="pb-2 px-3">
                <b-col md="6">
                  <b-list-group class="mb-3">
                    <b-list-group-item>
                      <strong>Course</strong>
                      <p>
                        {{ currentProject.course }}:
                        {{ currentProject.course_title }}
                        &nbsp;
                      </p>
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Developer information</strong>
                      <ul class="developer-info">
                        <li
                          v-for="developer in developerInformation"
                          :key="developer.id"
                        >
                          {{ developer.full_name }},
                          <em
                            ><span
                              v-html="formatEmplid(developer.emplid)"
                            ></span
                          ></em>
                          &nbsp;
                          <b-link :href="`mailto:${developer.email_address}`">
                            <b-icon icon="envelope"></b-icon>
                          </b-link>
                        </li>
                      </ul>
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Program & Academic director</strong>
                      <p>
                        {{ currentProject.program }}:
                        {{ currentProject.academic_director }}
                        &nbsp;
                        <b-link
                          :href="
                            `mailto:${currentProject.academic_director_email}`
                          "
                        >
                          <b-icon icon="envelope"></b-icon>
                        </b-link>
                      </p>
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Request type & duration</strong>
                      <p>
                        {{ currentProject.request_type }} /
                        {{ currentProject.course_duration }}
                        &nbsp;
                      </p>
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Status</strong>
                      <br />
                      <p>{{ currentProject.project_status }}</p>
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Delivery date</strong>
                      <br />
                      <p>
                        {{ currentProject.delivery_date.substring(0, 10) }}
                      </p>
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Target launch semester</strong>
                      <br />
                      <p>
                        {{ currentProject.target_launch_semester }}
                      </p>
                    </b-list-group-item>
                  </b-list-group>
                </b-col>
                <b-col md="6">
                  <b-list-group>
                    <b-list-group-item>
                      <strong>Last interaction date</strong>
                      <br />
                      <p v-if="currentProject.last_interaction_date">
                        {{
                          currentProject.last_interaction_date.substring(0, 10)
                        }}
                      </p>
                      <p v-else><b-icon icon="emoji-frown"></b-icon></p>
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>OFDIT lead</strong>
                      <p>
                        {{ currentProject.ofdit_lead }}
                        &nbsp;
                        <b-link
                          :href="`mailto:${currentProject.ofdit_lead_email}`"
                        >
                          <b-icon icon="envelope"></b-icon>
                        </b-link>
                      </p>
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Tags</strong>
                      <p v-if="currentProject.tags">
                        {{ currentProject.tags }}
                      </p>
                      <p v-else>
                        N/A
                      </p>
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Blackboard URL</strong>
                      <p>
                        <b-link
                          v-if="currentProject.bb_url"
                          :href="currentProject.bb_url"
                          :target="'_blank'"
                        >
                          <b-icon icon="link45deg"></b-icon>
                        </b-link>
                        <span v-else><b-icon icon="emoji-frown"></b-icon></span>
                        &nbsp;
                      </p>
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Course planning URL</strong>
                      <p>
                        <b-link
                          v-if="currentProject.course_planning_url"
                          :href="currentProject.course_planning_url"
                          :target="'_blank'"
                        >
                          <b-icon icon="link45deg"></b-icon>
                        </b-link>
                        <span v-else><b-icon icon="emoji-frown"></b-icon></span>
                        &nbsp;
                      </p>
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Review URL</strong>
                      <p>
                        <b-link
                          v-if="currentProject.review_url"
                          :href="currentProject.review_url"
                          :target="'_blank'"
                        >
                          <b-icon icon="link45deg"></b-icon>
                        </b-link>
                        <span v-else><b-icon icon="emoji-frown"></b-icon></span>
                        &nbsp;
                      </p>
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>Cohort</strong>
                      <p>{{ currentProject.cohort }}</p>
                    </b-list-group-item>
                  </b-list-group>
                </b-col>
              </b-row>
              <b-row class="pb-2 px-3">
                <b-col md="6">
                  <b-list-group class="mb-3">
                    <b-list-group-item>
                      <strong>Revision justification</strong>
                      <p v-if="currentProject.revision_justification">
                        {{ currentProject.revision_justification }}
                      </p>
                      <p v-else>N/A</p>
                    </b-list-group-item>
                    <b-list-group-item
                      ><strong>Developer justification</strong>
                      <p v-if="currentProject.developer_justification">
                        {{ currentProject.developer_justification }}
                      </p>
                      <p v-else>N/A</p></b-list-group-item
                    >
                  </b-list-group>
                </b-col>
                <b-col md="6">
                  <b-list-group class="mb-3">
                    <b-list-group-item>
                      <strong>Scope of work</strong>
                      <p v-if="currentProject.scope_of_work">
                        {{ currentProject.scope_of_work }}
                      </p>
                      <p v-else>N/A</p>
                    </b-list-group-item>
                    <b-list-group-item>
                      <strong>OFDIT notes</strong>
                      <p v-if="currentProject.ofdit_notes">
                        {{ currentProject.ofdit_notes }}
                      </p>
                      <p v-else>N/A</p></b-list-group-item
                    >
                  </b-list-group>
                </b-col>
              </b-row>
            </b-tab>
            <b-tab card="true" title="Interactions">
              <div>
                <b-button
                  class="mx-3 my-3"
                  @click="toggleCommentForm"
                  variant="outline-primary"
                >
                  <b-icon icon="chat-text"></b-icon>
                  Toggle comment entry form
                </b-button>
                <GCommentForm
                  class="pb-3"
                  v-if="showCommentForm"
                ></GCommentForm>
              </div>
              <b-table
                striped
                stacked="md"
                show-empty
                empty-text="🙊 There are no comments for this project."
                :sort-by.sync="sortBy"
                :sort-desc="sortDesc"
                :sort-compare="sortByDateThenID"
                :items="projectComments"
                :fields="fields"
              >
                <template #table-colgroup>
                  <col width="9%" />
                  <col width="13%" />
                  <col width="78%" />
                </template>
              </b-table>
            </b-tab>
          </b-tabs>
        </b-card>
        <b-row align-h="end">
          <b-button class="mt-3 mr-3" @click="closeDetails"
            >Return to {{ origin }}</b-button
          >
        </b-row>
      </b-col></b-row
    >
  </div>
</template>

<script>
  import GCommentForm from '@/components/GCommentForm.vue'
  import GProjectDetailsForm from '@/components/GProjectDetailsForm.vue'

  export default {
    name: 'GProjectDetails',
    components: {
      GCommentForm,
      GProjectDetailsForm,
    },
    props: {
      from: String,
    },
    created() {
      if (this.from === 'collection') {
        this.origin = 'previous page'
      }
    },
    data() {
      return {
        origin: 'Statusboard',
        activeTab: 1,
        sortBy: 'interaction_date',
        sortDesc: true,
        comment: {
          created_date: '',
          content: '',
        },
        fields: [
          {
            key: 'author',
            label: 'Author',
          },
          {
            key: 'interaction_date',
            label: 'Date',
            formatter: value => {
              return value.substring(0, 10)
            },
            sortable: true,
          },
          {
            key: 'content',
          },
        ],
      }
    },
    methods: {
      setupReview() {
        this.$router.push(`/reviews/setup/${this.currentProject.id}`)
      },
      closeDetails() {
        if (this.from === 'quicklook') {
          this.$router.push('/projects')
        } else if (this.from === 'collection') {
          this.$router.go(-1)
        } else {
          this.$store.commit('hideDetails')
        }
      },
      sortByDateThenID(aRow, bRow, key) {
        const a = aRow[key]
        const b = bRow[key]
        if (a === b) {
          return aRow['id'] < bRow['id'] ? -1 : aRow['id'] > bRow['id'] ? 1 : 0
        } else {
          return a < b ? -1 : a > b ? 1 : 0
        }
      },
      toggleCommentForm() {
        this.$store.dispatch('toggleCommentForm')
      },
      updateStatus(newStatus) {
        this.$store.dispatch('updateProjectStatus', {
          newStatus: newStatus,
        })
      },
      updateProjectDetails() {
        console.log(JSON.stringify(this.updatedProjectDetails))
      },
      resetProjectDetails() {
        this.updateProjectDetails = this.currentProject.delivery_date
      },
      formatEmplid(emplid) {
        const re = /\d{8}/
        if (re.test(emplid)) {
          return emplid
        } else {
          return '<i class="far fa-sad-tear">'
        }
      },
    },
    computed: {
      currentProject() {
        return this.$store.getters.currentProject
      },
      projectComments() {
        return this.$store.getters.projectComments
      },
      currentUser() {
        return this.$store.getters.currentUser
      },
      developerInformation() {
        return this.$store.getters.developerInformation
      },
      showCommentForm() {
        return this.$store.getters.showCommentForm
      },
      isOFDITLead() {
        if (this.currentUser.id === this.currentProject.ofdit_lead_id) {
          return true
        } else {
          return false
        }
      },
    },
  }
</script>
<style scoped>
  .developer-info {
    padding: 0;
    list-style-type: none;
  }
  .table.b-table.b-table-stacked-md > tbody > tr > [data-label]::before {
    font-weight: 500;
  }
  .card-body {
    padding: 0;
  }
  p {
    white-space: pre-line;
  }
</style>
